import { useState, useEffect, useRef, useCallback } from "react";

const CHAT_API_URL = "https://chat.digitrand.com";
const WS_URL = CHAT_API_URL.replace("http://", "ws://").replace(
  "https://",
  "wss://"
);

export const useSocketManager = ({ currentUser, activeUserId }) => {
  const [employees, setEmployees] = useState([]);
  const [onlineIds, setOnlineIds] = useState([]);
  const [conversations, setConversations] = useState({});
  const [socketConnected, setSocketConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef({});
  const messageQueueRef = useRef([]);
  const isConnectingRef = useRef(false);
  const connectionAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Helper: Check if user can see the message
  const isVisibleToUser = useCallback(
    (message) => {
      if (!currentUser?.id) return true;
      const isSender = message.senderId === currentUser.id;
      const isReceiver = message.receiverId === currentUser.id;
      if (!isSender && !isReceiver) return false;
      if (isSender && message.deletedForSender) return false;
      if (isReceiver && message.deletedForReceiver) return false;
      return true;
    },
    [currentUser?.id]
  );

  // Helper: Send message through WebSocket
  const sendWsMessage = useCallback((type, payload = {}) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({
        type,
        payload,
        timestamp: new Date().toISOString(),
      });
      console.log(`📤 Sending ${type}:`, payload);
      wsRef.current.send(message);
      return true;
    } else {
      console.warn(`⚠️ WebSocket not ready for ${type}`);
      // Queue message for later
      messageQueueRef.current.push({ type, payload });
      return false;
    }
  }, []);

  // Helper: Process queued messages
  const processMessageQueue = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    const queue = [...messageQueueRef.current];
    messageQueueRef.current = [];

    queue.forEach(({ type, payload }) => {
      sendWsMessage(type, payload);
    });
  }, [sendWsMessage]);

  // Create empty conversation structure
  const createEmptyConversation = useCallback(
    () => ({
      messages: [],
      typing: false,
      unread: 0,
      page: 1,
      hasMore: true,
      loaded: false,
    }),
    []
  );

  // Initialize WebSocket connection
  const initWebSocket = useCallback(() => {
    if (!currentUser?.id || isConnectingRef.current) return;

    console.log("🔌 Initializing WebSocket connection...");
    isConnectingRef.current = true;
    connectionAttemptsRef.current += 1;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Clear intervals
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // Create WebSocket connection
    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("✅ WebSocket connected");
        setSocketConnected(true);
        setConnectionError(null);
        isConnectingRef.current = false;
        connectionAttemptsRef.current = 0;

        // Send connection acknowledgement request
        const ackMessage = JSON.stringify({
          type: "ping", // यह connection test के लिए है
          timestamp: new Date().toISOString(),
        });
        ws.send(ackMessage);

        // Process queued messages
        setTimeout(() => processMessageQueue(), 500);

        // Start ping interval (every 30 seconds)
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            const pingMessage = JSON.stringify({
              type: "ping",
              timestamp: new Date().toISOString(),
            });
            ws.send(pingMessage);
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`📩 Received ${data.type}:`, data);

          switch (data.type) {
            case "connection_ack":
              // Connection acknowledged, now login
              setTimeout(() => {
                if (currentUser?.id && ws.readyState === WebSocket.OPEN) {
                  sendWsMessage("login", {
                    id: currentUser.id,
                    name: currentUser.name || "User",
                    email: currentUser.email || "",
                    photo: currentUser.photo?.trim() || null,
                    department: currentUser.department || "",
                    designation: currentUser.designation || "",
                    employee_code: currentUser.employee_code || "",
                  });
                }
              }, 1000);
              break;

            case "login_success":
              console.log("✅ Login successful:", data.userId);
              // Get online users after login
              setTimeout(() => sendWsMessage("get_online_users"), 1500);
              break;

            case "online_users":
            case "online_users_update":
              const onlineUserIds = (data.users || []).map((u) => u.userId);
              console.log("👥 Online users:", onlineUserIds.length);
              setOnlineIds(onlineUserIds);
              break;

            case "new_message":
              handleReceiveMessage(data);
              break;

            case "message_sent":
              handleMessageSentConfirmation(data);
              break;

            case "message_delivered":
              handleMessageDelivered(data);
              break;

            case "message_read":
              handleMessageRead(data);
              break;

            case "typing_indicator":
              handleTypingEvent(data);
              break;

            case "message_history":
              handleMessageHistory(data);
              break;

            case "mark_all_read_confirmation":
              console.log("✓ All messages marked as read");
              break;

            case "clear_chat_confirmation":
              handleClearChatConfirmation(data);
              break;

            case "pong":
              // Ping response, connection is alive
              break;

            case "error":
              console.error("❌ Server error:", data.errorMessage);
              setConnectionError(data.errorMessage);
              break;

            default:
              console.warn("⚠️ Unknown message type:", data.type);
          }
        } catch (error) {
          console.error("❌ Error parsing WebSocket message:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("🔴 WebSocket error:", error);
        setConnectionError("Connection error. Reconnecting...");
        setSocketConnected(false);
      };

      ws.onclose = (event) => {
        console.log("🔌 WebSocket closed:", event.code, event.reason);
        setSocketConnected(false);
        isConnectingRef.current = false;

        // Reconnect logic
        if (connectionAttemptsRef.current < maxReconnectAttempts) {
          console.log(
            `🔄 Reconnecting... attempt ${connectionAttemptsRef.current}`
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            initWebSocket();
          }, 3000);
        } else {
          setConnectionError("Cannot connect to server. Please refresh.");
        }
      };
    } catch (error) {
      console.error("❌ WebSocket creation error:", error);
      setConnectionError("Failed to create connection");
      isConnectingRef.current = false;
    }
  }, [currentUser, processMessageQueue, sendWsMessage]);

  // Handle incoming messages
  const handleReceiveMessage = useCallback(
    (data) => {
      if (!data.message || !currentUser?.id) return;

      const message = data.message;
      const otherId =
        message.senderId === currentUser.id
          ? message.receiverId
          : message.senderId;

      if (!otherId) return;

      const normalized = {
        _id: message.messageId || `msg_${Date.now()}`,
        messageId: message.messageId,
        senderId: message.senderId,
        receiverId: message.receiverId,
        message: message.content || "",
        type: message.type || "text",
        fileUrl: message.attachment?.url || null,
        fileName: message.attachment?.fileName || null,
        fileType: message.attachment?.fileType || null,
        createdAt: message.sentAt || new Date().toISOString(),
        status: message.status || "sent",
        timestamp: message.sentAt || new Date().toISOString(),
      };

      setConversations((prev) => {
        const conv = prev[otherId] || createEmptyConversation();

        // Check if message already exists
        if (conv.messages.some((m) => m.messageId === message.messageId)) {
          return prev;
        }

        return {
          ...prev,
          [otherId]: {
            ...conv,
            messages: [...conv.messages, normalized],
            unread: activeUserId === otherId ? 0 : conv.unread + 1,
          },
        };
      });

      // Send delivery receipt if we're the receiver
      if (message.senderId !== currentUser.id && message.messageId) {
        setTimeout(() => {
          sendWsMessage("delivery_receipt", { messageId: message.messageId });
        }, 500);
      }

      // Send read receipt if we're viewing this chat
      if (
        activeUserId === otherId &&
        message.senderId !== currentUser.id &&
        message.messageId
      ) {
        sendWsMessage("read_receipt", { messageId: message.messageId });
      }
    },
    [currentUser, activeUserId, createEmptyConversation, sendWsMessage]
  );

  // Message sent confirmation
  const handleMessageSentConfirmation = useCallback((data) => {
    if (!data?.messageId) return;

    setConversations((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((uid) => {
        updated[uid].messages = updated[uid].messages.map((m) => {
          if (m._id?.startsWith("temp_")) {
            return {
              ...m,
              _id: data.messageId,
              messageId: data.messageId,
              status: "sent",
            };
          }
          return m;
        });
      });
      return updated;
    });
  }, []);

  // Message delivered
  const handleMessageDelivered = useCallback((data) => {
    if (data?.messageId) {
      setConversations((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((uid) => {
          updated[uid].messages = updated[uid].messages.map((m) =>
            m.messageId === data.messageId ? { ...m, status: "delivered" } : m
          );
        });
        return updated;
      });
    }
  }, []);

  // Message read
  const handleMessageRead = useCallback((data) => {
    if (data?.messageId) {
      setConversations((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((uid) => {
          updated[uid].messages = updated[uid].messages.map((m) =>
            m.messageId === data.messageId ? { ...m, status: "read" } : m
          );
        });
        return updated;
      });
    }
  }, []);

  // Typing event
  const handleTypingEvent = useCallback(
    (data) => {
      if (!data?.senderId) return;

      setConversations((prev) => {
        const conv = prev[data.senderId] || createEmptyConversation();
        return {
          ...prev,
          [data.senderId]: {
            ...conv,
            typing: !!data.isTyping,
          },
        };
      });
    },
    [createEmptyConversation]
  );

  // Clear chat confirmation
  const handleClearChatConfirmation = useCallback(
    (data) => {
      const { otherUserId } = data;
      setConversations((prev) => {
        const updated = { ...prev };
        if (updated[otherUserId]) {
          updated[otherUserId] = createEmptyConversation();
        }
        return updated;
      });
    },
    [createEmptyConversation]
  );

  // Message history response
  const handleMessageHistory = useCallback((data) => {
    if (!data?.messages || !data?.pagination) return;

    // We'll handle this in loadHistory function
    console.log("📜 Message history received:", data.messages.length);
  }, []);

  // Initialize connection
  useEffect(() => {
    if (!currentUser?.id) return;

    initWebSocket();

    return () => {
      // Cleanup
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      setSocketConnected(false);
    };
  }, [currentUser?.id, initWebSocket]);

  // Send message
  const sendMessage = useCallback(
    (otherUserId, text, type = "text", fileUrl = null) => {
      if ((!text && !fileUrl) || !currentUser?.id || !socketConnected) {
        console.error("Cannot send message:", {
          socketConnected,
          hasText: !!text,
          hasFile: !!fileUrl,
        });
        return;
      }

      const tempId = `temp_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const messageData = {
        _id: tempId,
        senderId: currentUser.id,
        receiverId: otherUserId,
        message: text || "",
        type: fileUrl ? type : "text",
        fileUrl,
        createdAt: new Date().toISOString(),
        status: "sending",
      };

      // Add to UI immediately
      setConversations((prev) => {
        const conv = prev[otherUserId] || createEmptyConversation();
        return {
          ...prev,
          [otherUserId]: {
            ...conv,
            messages: [...conv.messages, messageData],
          },
        };
      });

      // Prepare payload
      const payload = {
        receiverId: otherUserId,
        content: text || "",
        messageType: fileUrl ? type : "text",
      };

      if (fileUrl) {
        payload.attachment = {
          url: fileUrl,
          fileName: "file",
          fileType: "application/octet-stream",
          fileSize: 0,
        };
      }

      // Send via WebSocket
      const sent = sendWsMessage("send_message", payload);
      if (!sent) {
        // If not sent, mark as failed
        setTimeout(() => {
          setConversations((prev) => {
            const conv = prev[otherUserId];
            if (!conv) return prev;
            return {
              ...prev,
              [otherUserId]: {
                ...conv,
                messages: conv.messages.map((m) =>
                  m._id === tempId ? { ...m, status: "error" } : m
                ),
              },
            };
          });
        }, 1000);
      }

      // Stop typing
      if (typingTimeoutRef.current[otherUserId]) {
        clearTimeout(typingTimeoutRef.current[otherUserId]);
        delete typingTimeoutRef.current[otherUserId];
      }
      sendWsMessage("typing", { receiverId: otherUserId, isTyping: false });
    },
    [currentUser, socketConnected, sendWsMessage, createEmptyConversation]
  );

  // Typing indicator
  const handleTyping = useCallback(
    (otherUserId, isTyping) => {
      if (!socketConnected) {
        console.warn("Cannot send typing: Socket not connected");
        return;
      }

      console.log("✏️ Typing:", otherUserId, isTyping);
      sendWsMessage("typing", { receiverId: otherUserId, isTyping });

      // Update UI immediately
      setConversations((prev) => {
        const conv = prev[otherUserId] || createEmptyConversation();
        return {
          ...prev,
          [otherUserId]: { ...conv, typing: isTyping },
        };
      });

      // Clear previous timeout
      if (typingTimeoutRef.current[otherUserId]) {
        clearTimeout(typingTimeoutRef.current[otherUserId]);
      }

      // Auto-stop typing after 3 seconds
      if (isTyping) {
        typingTimeoutRef.current[otherUserId] = setTimeout(() => {
          sendWsMessage("typing", { receiverId: otherUserId, isTyping: false });
          setConversations((prev) => {
            const conv = prev[otherUserId];
            if (!conv) return prev;
            return {
              ...prev,
              [otherUserId]: { ...conv, typing: false },
            };
          });
          delete typingTimeoutRef.current[otherUserId];
        }, 3000);
      }
    },
    [socketConnected, sendWsMessage, createEmptyConversation]
  );

  // File upload
  const handleFileUpload = useCallback(
    async (otherUserId, file) => {
      if (!file || !currentUser?.id) return;

      const tempId = `file_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const typeMap = {
        image: file.type.startsWith("image/"),
        video: file.type.startsWith("video/"),
        audio: file.type.startsWith("audio/"),
      };
      const type = typeMap.image
        ? "image"
        : typeMap.video
        ? "video"
        : typeMap.audio
        ? "audio"
        : "document";

      // Show upload progress
      setUploadingFiles((prev) => ({ ...prev, [tempId]: file.name }));
      setUploadProgress((prev) => ({ ...prev, [tempId]: 0 }));

      // Optimistic message
      const optimisticMsg = {
        _id: tempId,
        senderId: currentUser.id,
        receiverId: otherUserId,
        message: file.name,
        type,
        fileUrl: null,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        createdAt: new Date().toISOString(),
        status: "uploading",
      };

      setConversations((prev) => {
        const conv = prev[otherUserId] || createEmptyConversation();
        return {
          ...prev,
          [otherUserId]: {
            ...conv,
            messages: [...conv.messages, optimisticMsg],
          },
        };
      });

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(`${CHAT_API_URL}/upload`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) throw new Error(`Upload failed: ${response.status}`);

        const result = await response.json();

        if (result.success) {
          let fullUrl = result.file.url;
          if (fullUrl && !fullUrl.startsWith("http")) {
            fullUrl = fullUrl.startsWith("/")
              ? `${CHAT_API_URL}${fullUrl}`
              : `${CHAT_API_URL}/${fullUrl}`;
          }

          // Update message with actual URL
          setConversations((prev) => {
            const conv = prev[otherUserId];
            if (!conv) return prev;
            return {
              ...prev,
              [otherUserId]: {
                ...conv,
                messages: conv.messages.map((m) =>
                  m._id === tempId
                    ? {
                        ...m,
                        fileUrl: fullUrl,
                        status: "sent",
                        messageId: tempId,
                      }
                    : m
                ),
              },
            };
          });

          // Send message via WebSocket
          sendWsMessage("send_message", {
            receiverId: otherUserId,
            content: file.name,
            messageType: type,
            attachment: {
              url: fullUrl,
              fileName: result.file.fileName,
              fileType: result.file.fileType,
              fileSize: result.file.fileSize,
            },
          });
        }
      } catch (error) {
        console.error("❌ File upload error:", error);
        setConversations((prev) => {
          const conv = prev[otherUserId];
          if (!conv) return prev;
          return {
            ...prev,
            [otherUserId]: {
              ...conv,
              messages: conv.messages.map((m) =>
                m._id === tempId ? { ...m, status: "error" } : m
              ),
            },
          };
        });
      } finally {
        // Cleanup progress indicators
        setTimeout(() => {
          setUploadingFiles((prev) => {
            const newState = { ...prev };
            delete newState[tempId];
            return newState;
          });
          setUploadProgress((prev) => {
            const newState = { ...prev };
            delete newState[tempId];
            return newState;
          });
        }, 3000);
      }
    },
    [currentUser, sendWsMessage, createEmptyConversation]
  );

  // Load message history
  const loadHistory = useCallback(
    async (otherUserId, page = 1) => {
      if (!currentUser?.id || !otherUserId) return;

      try {
        const url = `${CHAT_API_URL}/messages/${currentUser.id}/${otherUserId}?page=${page}&limit=50`;
        const res = await fetch(url);

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const result = await res.json();

        if (!result.success)
          throw new Error(result.error || "Failed to load history");

        const normalizedMessages = result.messages
          .filter(isVisibleToUser)
          .map((msg) => ({
            _id: msg.messageId,
            messageId: msg.messageId,
            senderId: msg.senderId,
            receiverId: msg.receiverId,
            message: msg.content || "",
            type: msg.type || "text",
            fileUrl: msg.attachment?.url || null,
            fileName: msg.attachment?.fileName || null,
            fileType: msg.attachment?.fileType || null,
            createdAt: msg.sentAt,
            status: msg.status || "sent",
            timestamp: msg.sentAt,
          }))
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        setConversations((prev) => {
          const prevConv = prev[otherUserId] || createEmptyConversation();
          const merged =
            page === 1
              ? normalizedMessages
              : [...normalizedMessages, ...prevConv.messages];

          // Remove duplicates
          const unique = merged.filter(
            (msg, i, arr) =>
              arr.findIndex((m) => m.messageId === msg.messageId) === i
          );

          return {
            ...prev,
            [otherUserId]: {
              ...prevConv,
              messages: unique,
              page,
              hasMore: result.pagination?.hasNext || false,
              loaded: true,
            },
          };
        });
      } catch (error) {
        console.error("❌ Load history failed:", error);
      }
    },
    [currentUser, isVisibleToUser, createEmptyConversation]
  );

  // Get online users
  const getOnlineUsers = useCallback(() => {
    if (socketConnected) {
      sendWsMessage("get_online_users");
    }
  }, [socketConnected, sendWsMessage]);

  // Mark all as read
  const markAllAsRead = useCallback(
    (otherUserId) => {
      if (socketConnected) {
        sendWsMessage("mark_all_read", { otherUserId });
      }

      // Update UI immediately
      setConversations((prev) => {
        const conv = prev[otherUserId];
        if (!conv) return prev;
        return {
          ...prev,
          [otherUserId]: {
            ...conv,
            messages: conv.messages.map((m) =>
              m.status === "delivered" ? { ...m, status: "read" } : m
            ),
            unread: 0,
          },
        };
      });
    },
    [socketConnected, sendWsMessage]
  );

  // Clear chat
  const clearChat = useCallback(
    (otherUserId) => {
      if (socketConnected) {
        sendWsMessage("clear_chat", { otherUserId });
      }

      // Update UI immediately
      setConversations((prev) => {
        const updated = { ...prev };
        if (updated[otherUserId]) {
          updated[otherUserId] = createEmptyConversation();
        }
        return updated;
      });
    },
    [socketConnected, sendWsMessage, createEmptyConversation]
  );

  // Manual reconnect
  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    connectionAttemptsRef.current = 0;
    initWebSocket();
  }, [initWebSocket]);

  return {
    // State
    employees,
    setEmployees,
    onlineIds,
    conversations,
    setConversations,
    loadingEmployees,
    setLoadingEmployees,
    socketConnected,
    connectionError,
    uploadingFiles,
    uploadProgress,

    // Actions
    sendMessage,
    handleTyping,
    handleFileUpload,
    updateMessageStatus: (messageId, newStatus) => {
      setConversations((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((uid) => {
          updated[uid].messages = updated[uid].messages.map((msg) =>
            msg.messageId === messageId ? { ...msg, status: newStatus } : msg
          );
        });
        return updated;
      });
    },
    loadHistory,
    getOnlineUsers,
    markAllAsRead,
    clearChat,
    reconnect,
  };
};
